import React, {useRef, useState} from 'react';
import {Stack, Typography} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {t} from 'i18next';
import './Chip.scss';
import ArrIconButton from '../../../../../../common/arr-icon-button/ArrIconButton';
import ArrTooltip from '../../../../../../common/arr-tooltip/ArrTooltip';
import {Criteria} from '../../../../../types/Criteria';
import {
  getInvalidSavedSearchIds,
  hasSavedSearchBlockAtOrAboveThreshold,
  hasInvalidSavedSearchTokens,
  tokenizeSavedSearchChipValue,
} from '../../../../../utilities/savedSearchCriteria';
import {SAVED_SEARCH_NAME_CONVERSION_THRESHOLD} from '../../../../../../../config/env';
import ChipEditor from './chip-editor/ChipEditor';

const CLEAR_ICON_SPACE = 32;

export interface ChipProps {
  criteria: Criteria;
  onDelete?: (criteria: Criteria) => void;
  onEdit?: (criteria: Criteria) => void;
  dataTestId: string;
  chipFocusRef?: React.RefObject<HTMLElement | null>;
  index?: number;
  registerRef?: (el: HTMLElement | null) => void;
}

const Chip: React.FC<ChipProps> = ({
  criteria,
  onDelete,
  onEdit,
  dataTestId,
  chipFocusRef,
  index,
  registerRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [lockedWidth, setLockedWidth] = useState<number | null>(null);
  const [clearVisible, setClearVisible] = useState(false);
  const chipRef = useRef<HTMLElement | null>(null);
  const shouldRestoreFocus = useRef(false);
  const displayText = String(criteria.displayValue ?? criteria.value ?? '');
  const isThresholdWarning =
    criteria.type === 'Operand' &&
    typeof criteria.value === 'string' &&
    hasSavedSearchBlockAtOrAboveThreshold(
      criteria.value,
      SAVED_SEARCH_NAME_CONVERSION_THRESHOLD
    );
  const isErrorChip =
    criteria.type === 'Operand' &&
    Boolean(criteria.displayValue) &&
    hasInvalidSavedSearchTokens(displayText);
  const invalidSavedSearchIds = isErrorChip
    ? getInvalidSavedSearchIds(displayText)
    : [];
  const displayTokens = isErrorChip
    ? tokenizeSavedSearchChipValue(displayText)
    : null;
  const chipClassName = [
    criteria.type === 'Operand' ? 'operand-chip' : 'operator-chip',
    isThresholdWarning
      ? 'operand-chip--warning'
      : isErrorChip
        ? 'operand-chip--error'
        : '',
  ]
    .filter(Boolean)
    .join(' ');
  const chipTooltip = isThresholdWarning
    ? {
        message: t(
          'sessionSearch.chipEditor.savedSearchNameConversionThreshold',
          {
            threshold: SAVED_SEARCH_NAME_CONVERSION_THRESHOLD,
          }
        ),
      }
    : isErrorChip
      ? {
          message: t('sessionSearch.chipEditor.invalidSavedSearchId'),
          ids: t('sessionSearch.chipEditor.invalidSavedSearchIdList', {
            id: invalidSavedSearchIds.join(', '),
          }),
        }
      : undefined;
  const typographyTestId = `chip-typography-${dataTestId ?? (String(criteria.value ?? '').replaceAll(' ', '-') || 'chip')}`;
  const clearButtonTestId = `chip-clear-${dataTestId ?? (String(criteria.value ?? '').replaceAll(' ', '-') || 'chip')}`;

  const renderDisplayValue = () => {
    if (!displayTokens) {
      return displayText;
    }

    return displayTokens.map((token, tokenIndex) =>
      token.isError ? (
        <span
          key={`saved-search-error-token-${tokenIndex}`}
          className="saved-search-error-token"
        >
          {token.text}
        </span>
      ) : (
        <React.Fragment key={`saved-search-token-${tokenIndex}`}>
          {token.text}
        </React.Fragment>
      )
    );
  };

  const getFocusTarget = () => {
    return chipRef.current ?? null;
  };

  const closePopper = () => {
    setOpen(false);
    setAnchorEl(null);
    chipRef.current?.blur();
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (open) {
      closePopper();
    } else {
      shouldRestoreFocus.current = true;
      onEdit && onEdit(criteria);
      setAnchorEl(e.currentTarget);
      setOpen(true);
    }
  };

  const onMenuClose = () => {
    closePopper();
  };

  const handleSave = (updated: Criteria) => {
    const updatedCriteria = {...updated};
    if (updatedCriteria.value !== criteria.value) {
      delete updatedCriteria.displayValue;
    }
    onEdit && onEdit(updatedCriteria);
    closePopper();
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDelete && onDelete(criteria);
  };

  const showClear = () => {
    const chip = chipRef.current;
    const row = chip?.closest('.chip-row');
    if (!chip || !row) {
      setLockedWidth(null);
      setClearVisible(true);
      return;
    }

    const chipRect = chip.getBoundingClientRect();
    const available = row.getBoundingClientRect().right - chipRect.left;
    setLockedWidth(
      chipRect.width + CLEAR_ICON_SPACE > available
        ? Math.min(chipRect.width, available)
        : null
    );
    setClearVisible(true);
  };

  const hideClear = () => {
    setClearVisible(false);
    setLockedWidth(null);
  };

  const chip = (
    <Stack
      direction={'row'}
      spacing={1}
      className={chipClassName}
      sx={
        lockedWidth != null
          ? {
              width: lockedWidth,
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              alignSelf: 'flex-start',
            }
          : undefined
      }
      tabIndex={0}
      data-chip-index={index}
      ref={
        ((el: HTMLElement | null) => {
          chipRef.current = el;
          registerRef?.(el);
        }) as any
      }
      onMouseEnter={showClear}
      onMouseLeave={hideClear}
      onFocus={(e) => {
        if (chipFocusRef) {
          chipFocusRef.current = e.currentTarget;
        }
        showClear();
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          hideClear();
        }
      }}
      onClick={(e) => {
        if (chipFocusRef) {
          chipFocusRef.current = e.currentTarget;
        }
        handlePanelClick(e);
      }}
    >
      <Typography
        className="typo-text label3_size"
        data-testid={typographyTestId}
        sx={
          lockedWidth != null
            ? {minWidth: 0, flex: '1 1 auto', whiteSpace: 'normal'}
            : undefined
        }
      >
        {renderDisplayValue()}
      </Typography>
      <ArrIconButton
        icon={<ClearIcon />}
        ariaLabel=""
        onClick={handleDeleteClick}
        size="small"
        className="icon-clear"
        testId={clearButtonTestId}
        style={{display: clearVisible ? 'inline-flex' : 'none'}}
      />
    </Stack>
  );

  return (
    <>
      {chipTooltip ? (
        <ArrTooltip
          content={
            <span className="saved-search-error-tooltip-content">
              <span>{chipTooltip.message}</span>
              {'ids' in chipTooltip && (
                <span className="saved-search-error-id-list">
                  {chipTooltip.ids}
                </span>
              )}
            </span>
          }
          trigger="hover"
          placement="top"
          focusableWrapper
          tooltipKey={`${criteria.id}-saved-search-chip`}
        >
          <span className="operand-chip-tooltip-anchor">{chip}</span>
        </ArrTooltip>
      ) : (
        chip
      )}
      <ChipEditor
        anchorEl={anchorEl}
        open={open}
        criteria={criteria}
        onSave={handleSave}
        onClose={closePopper}
        onMenuClose={onMenuClose}
        anchorReturnRef={{current: getFocusTarget()} as any}
        restoreFocus={shouldRestoreFocus.current}
      />
    </>
  );
};

export default Chip;
